import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Tags, UploadCloud } from 'lucide-react';
import { Fragment, useCallback, useEffect, useState } from 'react';

import {
  type LabelingIngestResponse,
  type SkipReason,
  claimCard,
  fetchNextCard,
  skipCard,
  submitCardLabel,
  uploadLabelingDocuments,
} from '@/api/labeling';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { BrowseTab } from '@/labeling/BrowseTab';
import {
  DOCUMENT_TYPES,
  type FormValues,
  JURISDICTIONS,
  TAX_INDICATIONS,
  buildLabelPayload,
  emptyForm,
  formFromPrefill,
} from '@/labeling/labelForm';
import {
  FIELD_GUIDE,
  LABELING_CONVENTIONS,
  LABEL_TABS,
  type LabelTabKey,
} from '@/labeling/labelGuide';
import { cardQueryKey } from '@/labeling/labelingNav';
import { LabelingStatsPanel } from '@/labeling/LabelingStatsPanel';
import { type PreviewState, useCardPreview } from '@/labeling/useCardPreview';

import { useAuth } from '../auth/AuthContext';

// String-valued form keys only — excludes `absent_fields` (string[]), so
// `form[f.name]` stays a plain string for the text inputs.
type TextFieldName = {
  [K in keyof FormValues]: FormValues[K] extends string ? K : never;
}[keyof FormValues];

const TEXT_FIELDS: { name: TextFieldName; label: string; placeholder?: string }[] = [
  { name: 'supplier_name_on_invoice', label: 'Supplier name' },
  { name: 'invoice_date', label: 'Invoice date', placeholder: 'YYYY-MM-DD' },
  { name: 'invoice_total', label: 'Invoice total' },
  { name: 'supplier_bn', label: 'Supplier BN', placeholder: '123456789RT0001' },
  { name: 'tax_shown', label: 'Tax shown' },
  { name: 'invoice_number', label: 'Invoice number' },
  { name: 'recipient_name', label: 'Recipient name' },
  { name: 'payment_terms', label: 'Payment terms', placeholder: 'Net 30' },
];

// One-click triage skips, each tagged with why (stored on the document).
const SKIP_OPTIONS: { reason: SkipReason; label: string }[] = [
  { reason: 'foreign', label: 'US / foreign (no GST)' },
  { reason: 'unsupported_format', label: 'Not PDF/HTML' },
  { reason: 'unreadable', label: 'Unreadable' },
];

export function LabelingPage() {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // The invoice under label comes from the route: /labeling/$documentId targets
  // one specific card (deep link / Browse row / new tab); /labeling is the
  // queue's next card. `strict: false` because the same page serves both routes.
  const params = useParams({ strict: false }) as { documentId?: string };
  const selectedDocumentId = params.documentId ?? null;

  const cardQuery = useQuery({
    queryKey: cardQueryKey(selectedDocumentId),
    queryFn: async () => {
      const token = (await getIdToken()) ?? '';
      return selectedDocumentId ? claimCard(selectedDocumentId, token) : fetchNextCard(token);
    },
  });
  const card = cardQuery.data ?? null;

  // The card's lease is deliberately NOT released on navigation away — leaving
  // and returning to the page resumes the same card (claim_next_card returns
  // the caller's own unexpired-leased card first). An abandoned card returns
  // to the pool when its 15-minute lease expires; submit/skip clear it server
  // side.

  const [form, setForm] = useState<FormValues>(emptyForm);
  const [uploadResult, setUploadResult] = useState<LabelingIngestResponse | null>(null);

  // Re-seed the form from the parser prefill whenever a new card loads.
  useEffect(() => {
    setForm(formFromPrefill(card?.prefill));
  }, [card?.document_id, card?.prefill]);

  // Refresh the list + stats after every label. For the card itself: if a
  // specific doc was open (deep link / Browse), navigate back to the queue URL
  // — the route change swaps to the 'next' card query and fetches, so we must
  // NOT invalidate the selected-card key (that would re-claim the doc we just
  // finished). In queue mode, refetch 'next' and return its promise so the
  // controls stay disabled until the next card resolves.
  const advance = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['labeling', 'documents'] });
    queryClient.invalidateQueries({ queryKey: ['labeling', 'stats'] });
    if (selectedDocumentId) {
      return navigate({ to: '/labeling' });
    }
    return queryClient.invalidateQueries({ queryKey: cardQueryKey(null) });
  }, [queryClient, navigate, selectedDocumentId]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!card) return;
      const body = buildLabelPayload(form, card.prefill_source);
      return submitCardLabel(card.document_id, body, (await getIdToken()) ?? '');
    },
    onSuccess: advance,
  });

  const skip = useMutation({
    mutationFn: async (reason: SkipReason) => {
      if (!card) return;
      return skipCard(card.document_id, reason, (await getIdToken()) ?? '');
    },
    onSuccess: advance,
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) =>
      uploadLabelingDocuments(files, (await getIdToken()) ?? ''),
    onSuccess: (result) => {
      setUploadResult(result);
      return advance();
    },
  });

  const busy = submit.isPending || skip.isPending;
  const mutationError = submit.isError || skip.isError;

  const [tab, setTab] = useState<LabelTabKey>('label');

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 p-6">
      <header className="flex items-start gap-3">
        <Tags size={22} strokeWidth={1.75} className="mt-0.5 shrink-0 text-brand-royal" />
        <div>
          <h1 className="text-xl font-semibold text-brand-navy">Label invoices</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Build the shared gold corpus. The parser pre-fills each card — correct what it got
            wrong and submit. Every correction sharpens the field extractor. Multiple people can
            label at once; each gets a different card.
          </p>
        </div>
      </header>

      <TabBar tab={tab} onTab={setTab} />

      {tab === 'label' ? (
        <div className="flex flex-col gap-4">
          <LabelingGuide />
          {cardQuery.isLoading ? (
            <StatusCard>
              <Spinner /> Loading the next card…
            </StatusCard>
          ) : cardQuery.isError ? (
            <StatusCard>
              {selectedDocumentId ? (
                <>
                  <span className="text-destructive">
                    Couldn&apos;t open that document — it may already be labeled or held by
                    someone else.
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate({ to: '/labeling' })}
                  >
                    Back to the queue
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-destructive">Couldn&apos;t load the next card.</span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => cardQuery.refetch()}
                  >
                    Retry
                  </Button>
                </>
              )}
            </StatusCard>
          ) : card ? (
            <FlashCard
              card={card}
              form={form}
              setForm={setForm}
              onSubmit={() => submit.mutate()}
              onSkip={(reason) => skip.mutate(reason)}
              busy={busy}
              mutationError={mutationError}
              getIdToken={getIdToken}
            />
          ) : (
            <StatusCard>
              Nothing left to label right now. Add more from the{' '}
              <button
                type="button"
                className="font-medium text-brand-royal underline"
                onClick={() => setTab('upload')}
              >
                Upload
              </button>{' '}
              tab to keep going.
            </StatusCard>
          )}
        </div>
      ) : tab === 'browse' ? (
        <BrowseTab getIdToken={getIdToken} />
      ) : tab === 'upload' ? (
        <UploadZone
          onFiles={(files) => upload.mutate(files)}
          pending={upload.isPending}
          failed={upload.isError}
          result={uploadResult}
        />
      ) : (
        <LabelingStatsPanel />
      )}
    </div>
  );
}

function TabBar({ tab, onTab }: { tab: LabelTabKey; onTab: (t: LabelTabKey) => void }) {
  return (
    <div role="tablist" aria-label="Labeling views" className="flex gap-1 border-b border-border">
      {LABEL_TABS.map(({ key, label }) => {
        const active = key === tab;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              active
                ? 'border-brand-royal text-brand-navy'
                : 'border-transparent text-muted-foreground hover:text-brand-navy'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Collapsible labeler guide shown on the Label tab — conventions plus a
 *  one-line definition of every field, mirroring the pinned label schema. */
function LabelingGuide() {
  return (
    <details className="rounded-md border border-border bg-secondary/20 text-sm" open>
      <summary className="cursor-pointer px-4 py-2 font-medium text-brand-navy">
        How to label
      </summary>
      <div className="flex flex-col gap-3 px-4 pb-4 pt-1">
        <ul className="list-disc space-y-1 pl-5 text-xs text-brand-slate">
          {LABELING_CONVENTIONS.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <dl className="grid gap-x-4 gap-y-1.5 sm:grid-cols-[max-content_1fr]">
          {FIELD_GUIDE.map(({ field, label, help }) => (
            <Fragment key={field}>
              <dt className="text-xs font-medium text-brand-navy">{label}</dt>
              <dd className="text-xs text-brand-slate">{help}</dd>
            </Fragment>
          ))}
        </dl>
      </div>
    </details>
  );
}

function StatusCard({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-center gap-2 py-16 text-center text-sm text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}

function UploadZone({
  onFiles,
  pending,
  failed,
  result,
}: {
  onFiles: (files: File[]) => void;
  pending: boolean;
  failed: boolean;
  result: LabelingIngestResponse | null;
}) {
  const [dragging, setDragging] = useState(false);
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);

  return (
    <Card className={dragging ? 'border-brand-royal border-dashed' : 'border-dashed'}>
      <CardContent
        className="flex flex-col items-center justify-center gap-2 py-10 text-center"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const files = Array.from(e.dataTransfer.files);
          if (files.length) onFiles(files);
        }}
      >
        <UploadCloud size={30} strokeWidth={1.5} className="text-muted-foreground/70" aria-hidden />
        <p className="text-sm font-medium text-brand-navy">Drag &amp; drop invoices here</p>
        <p className="text-xs text-muted-foreground">PDF, HTML, or image — deduped automatically.</p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={pending}
          onClick={() => inputEl?.click()}
          className="mt-1 gap-2"
        >
          {pending ? <Spinner /> : null}
          {pending ? 'Uploading…' : 'Choose files'}
        </Button>
        <input
          ref={setInputEl}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) onFiles(files);
            e.target.value = '';
          }}
        />
        {failed ? (
          <p className="mt-1 text-xs text-destructive">Upload failed — try again.</p>
        ) : result ? (
          <p className="mt-1 text-xs text-brand-slate">
            {result.added} added · {result.duplicates} already in corpus · {result.skipped} skipped
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

type FlashCardProps = {
  card: NonNullable<Awaited<ReturnType<typeof fetchNextCard>>>;
  form: FormValues;
  setForm: React.Dispatch<React.SetStateAction<FormValues>>;
  onSubmit: () => void;
  onSkip: (reason: SkipReason) => void;
  busy: boolean;
  mutationError: boolean;
  getIdToken: () => Promise<string | null>;
};

function FlashCard({
  card,
  form,
  setForm,
  onSubmit,
  onSkip,
  busy,
  mutationError,
  getIdToken,
}: FlashCardProps) {
  const { state: preview, retry: retryPreview } = useCardPreview(card.document_id, getIdToken);

  const set = (name: keyof FormValues) => (value: string) =>
    setForm((prev) => ({ ...prev, [name]: value }) as FormValues);

  // Toggle a value field between "labeled" and "confirmed not on the document".
  // Marking absent clears the value (a field can't be both set and absent).
  const toggleAbsent = (name: keyof FormValues) => () =>
    setForm((prev) => {
      const marked = prev.absent_fields.includes(name);
      return {
        ...prev,
        absent_fields: marked
          ? prev.absent_fields.filter((f) => f !== name)
          : [...prev.absent_fields, name],
        [name]: marked ? prev[name] : '',
      } as FormValues;
    });

  return (
    <Card>
      <CardContent className="grid gap-6 pt-6 lg:grid-cols-2">
        {/* Preview + acquired text */}
        <div className="flex min-h-[26rem] flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="truncate text-sm font-medium text-brand-navy" title={card.filename}>
              {card.filename}
            </span>
            <span className="shrink-0 text-xs uppercase tracking-wide text-brand-slate/70">
              {card.file_type}
            </span>
          </div>
          <Preview card={card} preview={preview} onRetry={retryPreview} />
          <AcquiredText text={card.text} />
        </div>

        {/* Label form */}
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) onSubmit();
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !busy) {
              e.preventDefault();
              onSubmit();
            }
          }}
        >
          <SelectField
            label="Document type"
            value={form.document_type}
            options={DOCUMENT_TYPES}
            onChange={set('document_type')}
          />
          {TEXT_FIELDS.map((f) => (
            <TextField
              key={f.name}
              label={f.label}
              placeholder={f.placeholder}
              value={form[f.name]}
              onChange={set(f.name)}
              absent={form.absent_fields.includes(f.name)}
              onToggleAbsent={toggleAbsent(f.name)}
            />
          ))}
          <SelectField
            label="Tax indication"
            value={form.tax_indication}
            options={TAX_INDICATIONS}
            onChange={set('tax_indication')}
          />
          <SelectField
            label="Supplier jurisdiction"
            value={form.supplier_jurisdiction}
            options={JURISDICTIONS}
            onChange={set('supplier_jurisdiction')}
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-brand-navy">Notes</span>
            <textarea
              className="min-h-[3rem] rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={form.notes}
              onChange={(e) => set('notes')(e.target.value)}
            />
          </label>

          {card.prefill_source ? (
            <p className="text-xs text-brand-slate/70">
              Pre-filled by {card.prefill_source} — correct anything it missed.
            </p>
          ) : (
            <p className="text-xs text-brand-slate/70">
              No text layer — label from the preview image.
            </p>
          )}
          {mutationError ? (
            <p className="text-xs text-destructive">
              That didn&apos;t save — check your entries and try again.
            </p>
          ) : null}

          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={busy} className="gap-2">
              {busy ? <Spinner /> : null}
              Submit &amp; next
            </Button>
            <span className="ml-auto text-xs text-brand-slate/60">⌘/Ctrl + Enter to submit</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
            <span className="text-xs font-medium text-brand-slate/70">Skip:</span>
            {SKIP_OPTIONS.map((opt) => (
              <Button
                key={opt.reason}
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onSkip(opt.reason)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function Preview({
  card,
  preview,
  onRetry,
}: {
  card: FlashCardProps['card'];
  preview: PreviewState;
  onRetry: () => void;
}) {
  if (preview.status === 'error') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-md border border-border bg-secondary/20 text-sm text-muted-foreground">
        <span className="text-destructive">Couldn&apos;t load the preview.</span>
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  if (preview.status === 'loading') {
    return (
      <div className="flex flex-1 items-center justify-center rounded-md border border-border bg-secondary/20 text-sm text-muted-foreground">
        <Spinner /> <span className="ml-2">Loading preview…</span>
      </div>
    );
  }
  // Branch on the byte-derived content type the server actually returned —
  // never the stored file_type (untrusted for backfilled invoices).
  const contentType = preview.contentType;
  if (contentType.startsWith('image/')) {
    return (
      <img
        src={preview.url}
        alt={card.filename}
        className="max-h-[28rem] w-full rounded-md border border-border object-contain"
      />
    );
  }
  // A genuine PDF renders in Chrome's built-in viewer, which is already
  // isolated — a `sandbox=""` iframe *blocks* that viewer ("This page has been
  // blocked by Chrome"), so only a byte-verified application/pdf gets an
  // unsandboxed frame. Everything else (served as inert text/plain) stays
  // fully sandboxed.
  const sandbox = contentType === 'application/pdf' ? undefined : '';
  return (
    <iframe
      title={card.filename}
      src={preview.url}
      sandbox={sandbox}
      className="h-[28rem] w-full rounded-md border border-border bg-white"
    />
  );
}

function AcquiredText({ text }: { text: string | null }) {
  return (
    <details className="rounded-md border border-border bg-secondary/20 text-sm">
      <summary className="cursor-pointer px-3 py-2 font-medium text-brand-navy">
        Extracted text
      </summary>
      {text ? (
        <pre className="max-h-48 overflow-auto whitespace-pre-wrap px-3 pb-3 text-xs text-brand-slate">
          {text}
        </pre>
      ) : (
        <p className="px-3 pb-3 text-xs text-muted-foreground">
          No text layer — label from the preview above.
        </p>
      )}
    </details>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  absent = false,
  onToggleAbsent,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  absent?: boolean;
  onToggleAbsent?: () => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="flex items-center justify-between gap-2">
        <span className="font-medium text-brand-navy">{label}</span>
        {onToggleAbsent ? (
          <button
            type="button"
            onClick={onToggleAbsent}
            aria-pressed={absent}
            className={
              absent
                ? 'text-xs font-medium text-brand-royal'
                : 'text-xs text-muted-foreground hover:text-brand-navy'
            }
          >
            {absent ? '✓ not on document' : 'not on document'}
          </button>
        ) : null}
      </span>
      <input
        type="text"
        value={absent ? '' : value}
        placeholder={absent ? 'Not on document' : placeholder}
        disabled={absent}
        onChange={(e) => onChange(e.target.value)}
        className={`rounded-md border border-border bg-background px-3 py-2 text-sm ${
          absent ? 'opacity-50' : ''
        }`}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-brand-navy">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border bg-background px-3 py-2 text-sm"
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}
